<template>
  <div>
    <InventoryPageHeader>
        <template #title>
            <i class="fas fa-boxes mr-2 text-primary"></i>Delivery Batches
        </template>
        <template #search>
            <b-form-input v-model="search" placeholder="Search batch..." size="sm" @input="applyFilters" />
        </template>
        <template #actions>
            <base-button size="sm" type="primary" @click="showAddBatchModal = true">
            <i class="fas fa-plus"></i> Add Batch
            </base-button>
        </template>
        </InventoryPageHeader>

    <b-container fluid class="mt--6">
      <div class="d-flex justify-content-end mb-2">
        <b-button size="sm" variant="outline-secondary" @click="showFilters = !showFilters">
          <i class="fas fa-filter"></i> Filter
          <i :class="showFilters ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="ml-1"></i>
        </b-button>
      </div>

      <transition name="slide-fade">
        <div v-if="showFilters" class="card card-shadow mb-3">
          <div class="card-body py-2 px-3">
            <div class="filter-group">
              <label class="form-control-label small font-weight-bold text-uppercase text-muted">Year</label>
              <b-form-checkbox-group v-model="yearFilter" :options="yearOptions" @change="applyFilters" />
              <div class="active-filters mt-1">
                <b-badge v-for="y in yearFilter" :key="y" pill variant="info" class="mr-1" @click="removeYearFilter(y)" style="cursor:pointer;">
                  {{ y }} <i class="fas fa-times ml-1"></i>
                </b-badge>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <b-card no-body class="card-shadow">
        <b-card-header><i class="fas fa-list mr-2"></i>Batch List</b-card-header>
        <b-card-body class="p-0 table-responsive">
          <el-table :data="filteredBatches" @row-click="goToDetail" row-class-name="cursor-pointer" style="width:100%;" empty-text="No batch found">
            <el-table-column label="Batch Number">
              <template slot-scope="{ row }">
                <router-link :to="`/batches/${row.number}`" class="text-primary font-weight-bold">Batch {{ row.number }}</router-link>
              </template>
            </el-table-column>
            <el-table-column prop="quantity" label="Quantity" />
            <el-table-column prop="characteristics" label="Characteristics" />
            <el-table-column prop="deliveryDate" label="Delivery Date" />
            <el-table-column prop="history" label="History" />
            <el-table-column label="" width="50">
              <template slot-scope="{ row }">
                <div @click.stop="confirmDeleteBatch(row)">
                  <base-button size="sm" type="outline-danger" icon>
                    <i class="fas fa-trash-alt"></i>
                  </base-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </b-card-body>
      </b-card>

      <!-- ADD BATCH MODAL -->
      <modal :show="showAddBatchModal" @close="showAddBatchModal = false">
        <template v-slot:header><h5>Add New Batch</h5></template>
        <b-form-group label="Batch Number *">
          <b-form-input v-model="newBatch.number" placeholder="e.g. 6" />
        </b-form-group>
        <b-form-group label="Quantity">
          <b-form-input v-model="newBatch.quantity" type="number" placeholder="0" />
        </b-form-group>
        <b-form-group label="Characteristics">
          <b-form-input v-model="newBatch.characteristics" placeholder="e.g. US Weld, new buttons" />
        </b-form-group>
        <b-form-group label="Delivery Date">
          <b-form-input v-model="newBatch.deliveryDate" type="date" />
        </b-form-group>
        <b-form-group label="History / Notes">
          <b-form-textarea v-model="newBatch.history" rows="2" placeholder="Any notes about this batch" />
        </b-form-group>
        <template v-slot:footer>
          <base-button type="outline-secondary" @click="showAddBatchModal = false">Cancel</base-button>
          <base-button type="primary" :disabled="!newBatch.number.trim()" @click="addBatch">Add Batch</base-button>
        </template>
      </modal>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import InventoryPageHeader from '@/components/InventoryPageHeader.vue'

export default {
  name: 'BatchList',
components: { InventoryPageHeader },

  data() {
    return {
      search: '',
      showFilters: false,
      yearFilter: [],
      showAddBatchModal: false,
      newBatch: {
        number: '',
        quantity: 0,
        characteristics: '',
        deliveryDate: '',
        history: ''
      }
    }
  },
  computed: {
    ...mapState('inventory', ['batches']),
    yearOptions() {
      const years = [...new Set(this.batches.map(b => new Date(b.deliveryDate).getFullYear()))].sort()
      return years.map(y => y.toString())
    },
    filteredBatches() {
      let list = this.batches
      if (this.search) {
        const q = this.search.toLowerCase()
        list = list.filter(b => ('Batch ' + b.number).toLowerCase().includes(q) || b.characteristics.toLowerCase().includes(q))
      }
      if (this.yearFilter.length) {
        list = list.filter(b => this.yearFilter.includes(new Date(b.deliveryDate).getFullYear().toString()))
      }
      return list
    }
  },
async mounted() {
  await this.loadBatches()
},
  methods: {
  ...mapActions('inventory', {
  loadBatches: 'loadBatches',
  doAddBatch: 'addBatch',          // ← renamed
  deleteBatch: 'deleteBatch'
}),
    goToDetail(row) { this.$router.push({ name: 'batchDetail', params: { number: row.number } }) },
    applyFilters() {},
    removeYearFilter(y) { this.yearFilter = this.yearFilter.filter(v => v !== y) },

    addBatch() {
      const { number, quantity, characteristics, deliveryDate, history } = this.newBatch
      if (!number.trim()) return
      if (this.batches.some(b => b.number === number.trim())) {
        alert('Batch number already exists.')
        return
      }
      this.doAddBatch({
        number: number.trim(),
        quantity: parseInt(quantity) || 0,
        characteristics: characteristics.trim(),
        deliveryDate: deliveryDate || new Date().toISOString().split('T')[0],
        history: history.trim()
      })
      this.newBatch = { number: '', quantity: 0, characteristics: '', deliveryDate: '', history: '' }
      this.showAddBatchModal = false
    },

    async confirmDeleteBatch(batch) {
      if (!batch || !batch.number) return
      if (!confirm(`Delete batch ${batch.number}? This will not delete its devices.`)) return
      try {
        await this.deleteBatch(batch.number)   // calls Vuex action
        this.loadBatches()
      } catch (e) {
        alert('Delete failed: ' + e.message)
      }
    }
  }
}
</script>